import type {
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  SubscriptionStatusResponse,
  AnalysisHistoryItem,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://looksmaxing.aimobileapps.net';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new ApiError(response.status, `API error: ${response.statusText}`);
  }

  return response.json();
}

// Analysis API
export async function submitAnalysis(
  userId: string,
  frontPhoto: File,
  sidePhoto: File,
  gender: string,
  ratingType: string
): Promise<{ analysisId: string }> {
  const formData = new FormData();
  formData.append('userId', userId);
  formData.append('frontImage', frontPhoto);
  formData.append('sideImage', sidePhoto);
  formData.append('gender', gender);
  formData.append('ratingType', ratingType);

  const response = await fetch(`${API_BASE_URL}/api/analysis/new`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new ApiError(response.status, 'Failed to submit analysis');
  }

  return response.json();
}

export async function submitAnalysisBase64(
  userId: string,
  gender: string,
  ratingType: string,
  frontImage: string,
  sideImage: string
): Promise<void> {
  const normalize = (value: string) => {
    const prefixIndex = value.indexOf('base64,');
    return prefixIndex >= 0 ? value.slice(prefixIndex + 7) : value;
  };

  await fetchApi('/api/analysis/new', {
    method: 'POST',
    body: JSON.stringify({
      userId,
      gender,
      ratingType,
      frontImage: normalize(frontImage),
      angleImage: null,
      sideImage: normalize(sideImage),
    }),
  });
}

export async function getAnalysisHistory(
  userId: string
): Promise<{ items: AnalysisHistoryItem[] }> {
  return fetchApi(`/api/analysis/history?userId=${encodeURIComponent(userId)}`);
}

// checkAnalysisAccess removed for web flow

// Subscription API
export async function getSubscriptionStatus(
  userId: string
): Promise<SubscriptionStatusResponse> {
  return fetchApi(`/api/user/subscription?userId=${encodeURIComponent(userId)}`);
}

// Payment API
export async function createCheckoutSession(
  request: CheckoutSessionRequest
): Promise<CheckoutSessionResponse> {
  return fetchApi('/api/payments/checkout', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export interface StripePrice {
  id: string;
  unitAmount: number | null;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  productName: string | null;
}

export async function getStripePrices(): Promise<StripePrice[]> {
  return fetchApi('/api/payments/prices');
}

// Helper to create checkout URL
export async function initiateCheckout(
  userId: string,
  plan: 'weekly' | 'yearly'
): Promise<string> {
  const priceId =
    plan === 'weekly'
      ? import.meta.env.VITE_STRIPE_PRICE_WEEKLY
      : import.meta.env.VITE_STRIPE_PRICE_YEARLY;

  const basePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  const successUrl = `${window.location.origin}${basePath}/results/history?session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${window.location.origin}${basePath}/paywall`;

  const { checkoutUrl } = await createCheckoutSession({
    userId,
    priceId,
    successUrl,
    cancelUrl,
  });

  return checkoutUrl;
}

export async function initiateCheckoutWithPriceId(
  userId: string,
  priceId: string
): Promise<string> {
  const basePath = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  const successUrl = `${window.location.origin}${basePath}/results/history?session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = `${window.location.origin}${basePath}/paywall`;

  const { checkoutUrl } = await createCheckoutSession({
    userId,
    priceId,
    successUrl,
    cancelUrl,
  });

  return checkoutUrl;
}
